import { useState } from "react";
import { useTeamSessions, useTeamOverview } from "@/lib/teamRecordQueries";
import AddTeamSessionsModal from "../AddTeamSessionsModal";
import BookTeamSessionModal from "../BookTeamSessionModal";
import StartTeamSessionModal from "../StartTeamSessionModal";

const ROOM_STATE_LABELS = {
  booked: "Upcoming", join_window: "Starting soon", live: "Live", pending_confirmation: "Pending",
  ended: "Completed", cancelled: "Cancelled",
};

/**
 * Clients Restructure — Team 360 -> Sessions. Every listed Session is
 * ONE canonical Session shared by every selected participant — never
 * one row per member (see session_repository.book_team_session_directly).
 */
export default function SessionsTab({ workspaceId }) {
  const { data, isPending, isError } = useTeamSessions(workspaceId);
  const { data: overview } = useTeamOverview(workspaceId);
  const [modal, setModal] = useState(null);
  const teamMembers = data?.team_members || [];
  const items = data?.items || [];
  const booked = items.filter((s) => ["booked", "join_window", "live", "pending_confirmation"].includes(s.room_state));
  const completed = items.filter((s) => s.room_state === "ended");

  return (
    <div data-testid="team-360-sessions-tab" className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-4 text-sm text-ink-soft">
          <span data-testid="team-sessions-available">{data?.available ?? 0} available</span>
          <span data-testid="team-sessions-booked-count">{data?.booked_count ?? 0} booked</span>
          <span data-testid="team-sessions-completed-count">{data?.completed_count ?? 0} completed</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setModal("add")}
            className="text-sm font-medium border border-ink/15 px-3 py-1.5 rounded-full text-ink hover:bg-ink/5 transition-colors"
            data-testid="team-sessions-add-btn"
          >
            + Add Sessions
          </button>
          <button
            onClick={() => setModal("start")}
            className="text-sm font-medium border border-ink/15 px-3 py-1.5 rounded-full text-ink hover:bg-ink/5 transition-colors"
            data-testid="team-sessions-start-now-btn"
          >
            Start Session Now
          </button>
          <button
            onClick={() => setModal("book")}
            className="text-sm font-medium bg-terracotta text-paper px-3 py-1.5 rounded-full hover:bg-terracotta/90 transition-colors"
            data-testid="team-sessions-book-btn"
          >
            Book Session
          </button>
        </div>
      </div>

      {isPending ? (
        <p className="text-sm text-ink-soft" data-testid="team-sessions-loading">Loading sessions…</p>
      ) : isError ? (
        <p className="text-sm text-red-600" data-testid="team-sessions-error">Couldn't load team sessions.</p>
      ) : (
        <>
          <div>
            <h2 className="font-serif text-lg text-ink mb-2">Booked/upcoming</h2>
            {booked.length === 0 ? (
              <p className="text-sm text-ink-soft" data-testid="team-sessions-booked-empty">No booked team sessions.</p>
            ) : (
              <div className="space-y-2" data-testid="team-sessions-booked-list">
                {booked.map((s) => (
                  <div key={s.ses_id} className="border border-ink/10 rounded-lg p-3 bg-white/50 flex items-center justify-between" data-testid={`team-session-${s.ses_id}`}>
                    <div>
                      <p className="text-sm font-medium text-ink">{new Date(s.starts_at).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                      <p className="text-xs text-ink-soft">{s.participant_member_ids.length} participants</p>
                    </div>
                    <span className="text-[10px] uppercase text-ink-soft border border-ink/10 rounded-full px-2 py-0.5">{ROOM_STATE_LABELS[s.room_state] || s.room_state}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="font-serif text-lg text-ink mb-2">Completed</h2>
            {completed.length === 0 ? (
              <p className="text-sm text-ink-soft" data-testid="team-sessions-completed-empty">No completed team sessions yet.</p>
            ) : (
              <div className="space-y-2" data-testid="team-sessions-completed-list">
                {completed.map((s) => (
                  <div key={s.ses_id} className="border border-ink/10 rounded-lg p-3 bg-white/50" data-testid={`team-session-completed-${s.ses_id}`}>
                    <p className="text-sm text-ink">{new Date(s.starts_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {modal === "add" && <AddTeamSessionsModal workspaceId={workspaceId} onClose={() => setModal(null)} />}
      {modal === "book" && (
        <BookTeamSessionModal workspaceId={workspaceId} teamName={overview?.team_name} teamMembers={teamMembers} onClose={() => setModal(null)} />
      )}
      {modal === "start" && (
        <StartTeamSessionModal workspaceId={workspaceId} teamMembers={teamMembers} onClose={() => setModal(null)} />
      )}
    </div>
  );
}
